import {
    ActionIcon,
    Box,
    Button,
    Container,
    Flex,
    lighten,
    rem,
    rgba,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import { IconMessages, IconTrash } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChatEntity } from "../db";
import { useChatId } from "../hooks/useChatId";
import { useChatSearch } from "../hooks/useChatSearch";
import { DeleteChatModal } from "./DeleteChatModal";
import { MainLink } from "./MainLink";

// Configuration constants
const INITIAL_CHATS = 10;
const CHATS_INCREMENT = 10;
const MAX_CHATS = 50;

export const Chats = ({
    search,
    chats,
    onChatSelect,
}: {
    search: string;
    chats?: ChatEntity[];
    onChatSelect?: (chatId: string) => void;
}) => {
    const chatId = useChatId();
    const [displayLimit, setDisplayLimit] = useState(INITIAL_CHATS);
    const theme = useMantineTheme();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const { colorScheme } = useMantineColorScheme();

    const filteredChats = useChatSearch(chats, search);
    const displayedChats = filteredChats.slice(0, displayLimit);
    const hasMore = filteredChats.length > displayLimit;
    const hasLess = displayLimit > INITIAL_CHATS;

    // Scroll to active chat when changed
    useEffect(() => {
        if (chatId && scrollRef.current) {
            const activeElement = scrollRef.current.querySelector(`[data-chat-id="${chatId}"]`);
            if (activeElement) {
                activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [chatId]);

    const handleLoadMore = () => {
        setDisplayLimit((prev) => Math.min(prev + CHATS_INCREMENT, MAX_CHATS));
    };

    const handleLoadLess = () => {
        setDisplayLimit((prev) => Math.max(prev - CHATS_INCREMENT, INITIAL_CHATS));
    };

    const chatItemStyle = (highlighted: boolean) => ({
        marginTop: rem(1),
        borderLeft: `6px solid ${rgba(theme.colors.purple[1], 0)}`,
        transition: "all 0.2s",
        cursor: "pointer",
        ...(highlighted && {
            borderLeft: `6px solid ${lighten(theme.colors.purple[4], 0.2)}`,
            backgroundColor: rgba(theme.colors.purple[7], 0.7),
            "& .mantineUnstyledButtonRoot": {
                color: "white",
            },
            "& .mantineTextRoot": {
                color: "white !important",
            },
            "& .mantineThemeIconRoot": {
                backgroundColor: theme.colors.purple[4],
            },
        }),
    });

    const buttonContainerStyle = useMemo(
        () =>
            ({
                borderTop: `1px solid ${theme.colors.gray[3]}`,
                padding: "8px 0",
                marginTop: "8px",
                backgroundColor: colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            }) as const,
        [colorScheme, theme],
    );

    return (
        <Container
            py="xs"
            px="0"
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            <Box
                ref={scrollRef}
                style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "4px",
                    "&::WebkitScrollbar": {
                        width: "8px",
                    },
                    "&::WebkitScrollbarTrack": {
                        background: "transparent",
                    },
                    "&::WebkitScrollbarThumb": {
                        background: theme.colors.gray[3],
                        borderRadius: "4px",
                    },
                    "&::WebkitScrollbarThumb:hover": {
                        background: theme.colors.gray[5],
                    },
                }}
            >
                {displayedChats.length > 0 ? (
                    displayedChats.map((chat) => {
                        const isActive = chatId === chat.id;
                        const isHighlighted = isActive || hoveredId === chat.id;

                        // Render content - either as a Link or clickable div based on onChatSelect
                        const chatContent = (
                            <MainLink
                                icon={<IconMessages size="1rem" />}
                                chat={chat}
                                isHighlighted={isHighlighted}
                            />
                        );

                        return (
                            <Flex
                                key={chat.id}
                                style={isHighlighted ? chatItemStyle(isHighlighted) : undefined}
                                data-testid={`chat-${chat.id}`}
                                data-chat-id={chat.id}
                                onMouseEnter={() => setHoveredId(chat.id)}
                                onMouseLeave={() => setHoveredId(null)}
                            >
                                {onChatSelect ? (
                                    <Box
                                        onClick={() => onChatSelect(chat.id)}
                                        style={{ flex: 1, cursor: "pointer" }}
                                    >
                                        {chatContent}
                                    </Box>
                                ) : (
                                    <Link to={`/chats/${chat.id}`} style={{ flex: 1 }}>
                                        {chatContent}
                                    </Link>
                                )}
                                <ActionIcon
                                    aria-label="Delete"
                                    data-testid="delete-chat"
                                    size="lg"
                                    variant={isActive ? "filled" : "light"}
                                    style={{
                                        backgroundColor: "transparent",
                                        alignSelf: "center",
                                        cursor: "pointer",
                                        marginLeft: rem(8),
                                    }}
                                >
                                    <DeleteChatModal chat={chat}>
                                        <IconTrash
                                            color={isHighlighted ? "white" : "black"}
                                            size="1rem"
                                        />
                                    </DeleteChatModal>
                                </ActionIcon>
                            </Flex>
                        );
                    })
                ) : (
                    <div
                        style={{
                            padding: rem(20),
                            textAlign: "center",
                            color: theme.colors.gray[6],
                        }}
                    >
                        No chat history found. Start a new conversation to see it here.
                    </div>
                )}
            </Box>

            {(hasMore || hasLess) && (
                <Flex justify="center" gap="sm" style={buttonContainerStyle}>
                    {hasLess && (
                        <Button variant="outline" size="xs" onClick={handleLoadLess}>
                            Show Less ({displayLimit - CHATS_INCREMENT})
                        </Button>
                    )}
                    {hasMore && (
                        <Button variant="outline" size="xs" onClick={handleLoadMore}>
                            Show More (
                            {Math.min(displayLimit + CHATS_INCREMENT, filteredChats.length)})
                        </Button>
                    )}
                </Flex>
            )}
        </Container>
    );
};
